import React, { useEffect, useState } from "react";
import { Accordion, Alert, Button, Form, ListGroup, Modal, Spinner } from "react-bootstrap";
import { FiList } from "react-icons/fi";
import StatusPill from "@core/components/StatusPill";
import { ErrorResponse } from "@core/types/errorResponse";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useFinanceDeductibleAction } from "@features/admin/finance/deductible/hooks/useFinanceDeductibleAction";
import { fetchFinanceDeductibleAsync } from "@features/admin/finance/deductible/services/financeDeductibleSvc";
import {
    FinanceDeductible,
    FinanceDeductibleDetail,
} from "@features/admin/finance/deductible/types/financeDeductible";

type FieldErrors = Partial<Record<string, string[]>>;

interface FinanceDeductibleFormModalProps {
    show: boolean;
    onHide: () => void;
    /** Null for create; the row being edited otherwise. */
    deductible: FinanceDeductible | null;
    onSuccess?: () => void | Promise<void>;
}

const UsedBySection: React.FC<{ detail: FinanceDeductibleDetail }> = ({ detail }) => {
    const { categories, rules, layouts = [], used_in_past_versions } = detail.used_by;
    const inactive = (active: boolean) => (active ? null : <span className="text-muted small ms-1">(inactive)</span>);

    return (
        <div data-testid="deductible-used-by">
            <h6 className="fw-semibold mb-2 text-muted text-uppercase small">Used by</h6>
            <Accordion alwaysOpen flush className="border rounded">
                <Accordion.Item eventKey="categories">
                    <Accordion.Header data-testid="deductible-used-by-categories-toggle">
                        Categories ({categories.length})
                    </Accordion.Header>
                    <Accordion.Body className="p-0">
                        {categories.length === 0 ? (
                            <div className="text-muted small p-3">Not configured on any category.</div>
                        ) : (
                            <ListGroup variant="flush">
                                {categories.map((c) => (
                                    <ListGroup.Item key={c.category_group_id} className="small">
                                        {c.category_name}{inactive(c.is_active)}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="rules">
                    <Accordion.Header data-testid="deductible-used-by-rules-toggle">
                        Allocation rules ({rules.length})
                    </Accordion.Header>
                    <Accordion.Body className="p-0">
                        {rules.length === 0 ? (
                            <div className="text-muted small p-3">Not configured on any allocation rule.</div>
                        ) : (
                            <ListGroup variant="flush">
                                {rules.map((r) => (
                                    <ListGroup.Item key={r.rule_group_id} className="small">
                                        {r.rule_name}{inactive(r.is_active)}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
                {layouts.length > 0 && (
                    <Accordion.Item eventKey="layouts">
                        <Accordion.Header data-testid="deductible-used-by-layouts-toggle">
                            Presentation layouts ({layouts.length})
                        </Accordion.Header>
                        <Accordion.Body className="p-0">
                            <ListGroup variant="flush">
                                {layouts.map((l) => (
                                    <ListGroup.Item key={l.presentation_layout_id} className="small">
                                        {l.layout_name} <span className="text-muted">({l.department_name})</span>{inactive(l.is_active)}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>
                )}
            </Accordion>
            {used_in_past_versions && (
                <div className="small text-muted mt-2" data-testid="deductible-used-in-past-versions">
                    Also used in past versions, so it cannot be deleted. Deactivate it instead.
                </div>
            )}
        </div>
    );
};

const FinanceDeductibleFormModal: React.FC<FinanceDeductibleFormModalProps> = ({ show, onHide, deductible, onSuccess }) => {
    const nationId = useScopeId() ?? 0;
    const isEdit = deductible !== null;
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const [detail, setDetail] = useState<FinanceDeductibleDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doCreate, doUpdate } = useFinanceDeductibleAction();

    useEffect(() => {
        if (!show) return;
        setName(deductible?.name ?? "");
        setDescription(deductible?.description ?? "");
        setFormErrors({});
        setDetail(null);

        if (!deductible) return;
        let cancelled = false;
        setDetailLoading(true);
        fetchFinanceDeductibleAsync(nationId, deductible.deductible_id)
            .then((response) => { if (!cancelled) setDetail(response.getData()); })
            .finally(() => { if (!cancelled) setDetailLoading(false); });
        return () => { cancelled = true; };
    }, [show, deductible, nationId]);

    const handleSubmit = async () => {
        if (!name.trim()) {
            setFormErrors({ name: ["Name is required."] });
            showValidation(["Name is required."]);
            return;
        }

        const payload = { name: name.trim(), description: description.trim() || null };
        try {
            const response = isEdit
                ? await doUpdate(nationId, deductible!.deductible_id, payload)
                : await doCreate(nationId, payload);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "");
                onHide();
                await onSuccess?.();
            } else {
                const error = response.getError() as ErrorResponse;
                const fieldErrors = (error?.errors as FieldErrors) ?? {};
                setFormErrors(fieldErrors);
                const list = Object.values(fieldErrors).flat().filter(Boolean) as string[];
                showValidation(list.length ? list : [error?.detail || "Failed to save finance deductible."]);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered data-testid="finance-deductible-form-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Modal.Title className="fw-semibold mb-0">
                        <FiList className="me-2" aria-hidden />
                        {isEdit ? "Edit Deductible" : "Create Deductible"}
                    </Modal.Title>
                    {deductible && <StatusPill active={deductible.is_active} />}
                </div>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <Form onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }}>
                    <Form.Group className="mb-3" controlId="deductible_name">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={100}
                            placeholder="e.g. Building Fund"
                            value={name}
                            onChange={(e) => { setName(e.target.value); setFormErrors((p) => ({ ...p, name: undefined })); }}
                            isInvalid={!!formErrors.name}
                            data-testid="deductible-name-input"
                        />
                        <Form.Control.Feedback type="invalid">{formErrors.name?.[0]}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="deductible_description">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            maxLength={500}
                            placeholder="Enter description (optional)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            isInvalid={!!formErrors.description}
                            data-testid="deductible-description-input"
                        />
                        <Form.Control.Feedback type="invalid">{formErrors.description?.[0]}</Form.Control.Feedback>
                    </Form.Group>

                    {isEdit && detailLoading && (
                        <div className="d-flex align-items-center small text-muted">
                            <Spinner animation="border" size="sm" className="me-2" />Loading usage…
                        </div>
                    )}
                    {isEdit && detail && <UsedBySection detail={detail} />}
                    {isEdit && !detailLoading && !detail && (
                        <Alert variant="light" className="small mb-0">Usage could not be loaded.</Alert>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={busy} aria-busy={busy} data-testid="deductible-save-btn">
                    {busy ? (<><Spinner animation="border" size="sm" className="me-2" />Saving…</>) : (isEdit ? "Save Changes" : "Create Deductible")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FinanceDeductibleFormModal;
